import { Check, Copy, Minus, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DataTableRowActionItem } from "./data-table-types";

const MENU_MIN_WIDTH = 140;
const MENU_EST_HEIGHT = 132;
const VIEWPORT_PAD = 8;

function computeMenuPosition(trigger: HTMLElement, menuHeight: number): { top: number; left: number } {
  const rect = trigger.getBoundingClientRect();
  const gap = 4;

  let left = rect.right - MENU_MIN_WIDTH;
  let top = rect.bottom + gap;

  left = Math.max(VIEWPORT_PAD, Math.min(left, globalThis.innerWidth - MENU_MIN_WIDTH - VIEWPORT_PAD));

  if (top + menuHeight > globalThis.innerHeight - VIEWPORT_PAD) {
    top = rect.top - gap - menuHeight;
  }
  top = Math.max(VIEWPORT_PAD, top);

  return { top, left };
}

export function DataTableRowMenu<T>({
  row,
  open,
  onToggle,
  onClose,
  onEdit,
  onDuplicate,
  onDelete,
  extraItems = [],
}: Readonly<{
  row: T;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit?: (row: T) => void;
  onDuplicate?: (row: T) => void;
  onDelete?: (row: T) => void;
  extraItems?: DataTableRowActionItem[];
}>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const visibleExtra = extraItems.filter((item) => !item.hidden);
  const hasAny = Boolean(onEdit || onDuplicate || onDelete || visibleExtra.length > 0);
  if (!hasAny) return null;

  const menuItemCount =
    (onEdit ? 1 : 0) + (onDuplicate ? 1 : 0) + visibleExtra.length + (onDelete ? 1 : 0);
  const menuHeight = Math.max(MENU_EST_HEIGHT, menuItemCount * 36 + 8);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    setMenuPos(computeMenuPosition(triggerRef.current, menuHeight));
  }, [menuHeight]);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    reposition();
    globalThis.addEventListener("scroll", reposition, true);
    globalThis.addEventListener("resize", reposition);
    return () => {
      globalThis.removeEventListener("scroll", reposition, true);
      globalThis.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;

    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            className="fixed z-[10050] min-w-[140px] overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-xl"
            style={{ top: menuPos.top, left: menuPos.left }}
            role="menu"
          >
            {onEdit && (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white/85 hover:bg-white/5"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  onEdit(row);
                }}
              >
                <Pencil size={14} className="text-white/45" />
                Edit
              </button>
            )}
            {onDuplicate && (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white/85 hover:bg-white/5"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  onDuplicate(row);
                }}
              >
                <Copy size={14} className="text-white/45" />
                Duplicate
              </button>
            )}
            {visibleExtra.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-40 ${
                  item.variant === "danger"
                    ? "text-red-300 hover:bg-red-500/10"
                    : "text-white/85 hover:bg-white/5"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.disabled) return;
                  onClose();
                  item.onClick();
                }}
              >
                {item.label}
              </button>
            ))}
            {onDelete && (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/10"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  onDelete(row);
                }}
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative inline-flex" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Row actions"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="flex h-7 w-7 items-center justify-center rounded-lg text-white/45 transition-colors hover:bg-white/8 hover:text-white"
      >
        <MoreVertical size={14} />
      </button>
      {menu}
    </div>
  );
}

export function DataTableCheckbox({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
}: Readonly<{
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  ariaLabel: string;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isOn = checked || indeterminate;

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  return (
    <label
      className="group relative inline-flex shrink-0 cursor-pointer"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        aria-label={ariaLabel}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={`flex h-4 w-4 items-center justify-center rounded-[5px] border transition-all duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-pink/50 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-surface active:scale-95 ${
          isOn
            ? "border-transparent bg-gradient-brand shadow-sm shadow-brand-pink/25"
            : "border-white/12 bg-white/[0.04] group-hover:border-brand-pink/35 group-hover:bg-white/[0.08]"
        }`}
      >
        {indeterminate && <Minus size={10} strokeWidth={3} className="text-white" aria-hidden />}
        {!indeterminate && checked && <Check size={10} strokeWidth={3} className="text-white" aria-hidden />}
      </span>
    </label>
  );
}
